import { produce, generateNanoId, type ChatMessage, type ChatSession, generateLocalIdByRole, compileGptMessages, nextApiChatTitleGenerate, openAiChatModels, chatSessionListDefault, defaultModelGptLimit, countTokens, messageToMessageToken, messageContentTokens, type ChatModel, getGPTContentToTextString, type GPTChatMessage, aiChatRequestHandler, getModelProvider, supportsToolCalling, getMCPManager, type ToolCall, DEFAULT_MODEL } from "@acme/util";
import { type Getter, atom } from "jotai";
import { atomWithStorage } from "jotai/utils"
import { toast } from "@acme/ui";
import { apiKeyAnthropicAtom, apiKeyAtom, apiKeyGoogleAtom, apiKeyXaiAtom, apiKeyDeepseekAtom, apiKeyGlmAtom, apiKeyKimiAtom, apiKeyQwenAtom, chatDefaultSettingsAtom, generalDefaultSettingsAtom, type AllApiKeys } from "./setting";
import { type RouterInputs } from "@acme/client";
import { apiKeyModalAtom, teamActiveAtom, userAtom } from "./user";
import { handleSendMessageError, handleSendMessageFetchClient, handleSendMessageFetchCloud, handleSendMessageInit, handleSendMessageInitCloud, handleSendMessageOnMessage, handleSendMessagePostFetch, buildToolSet, buildAgentSystemPrompt, isConfirmationRequiredResult, type ToolsForMessage } from "./function/chat-util";
import { globalToolsConfigAtom, globalAgentConfigAtom, activeAgentModeAtom, mcpServersConfigAtom } from "./instruction";

// Helper to get all API keys from store
export const getAllApiKeys = (get: Getter): AllApiKeys => ({
  openai: get(apiKeyAtom)?.key || '',
  anthropic: get(apiKeyAnthropicAtom)?.key || '',
  google: get(apiKeyGoogleAtom)?.key || '',
  xai: get(apiKeyXaiAtom)?.key || '',
  deepseek: get(apiKeyDeepseekAtom)?.key || '',
  glm: get(apiKeyGlmAtom)?.key || '',
  kimi: get(apiKeyKimiAtom)?.key || '',
  qwen: get(apiKeyQwenAtom)?.key || '',
});

export const playNotificationSound = (get: Getter) => {
  const { soundCompletion = true } = get(generalDefaultSettingsAtom);
  if (soundCompletion) {
    const audio = new Audio('/notification1.mp3');
    void audio.play();
  }
};

//~ ATOM
export const chatSessionActiveIdAtom = atom<string | null>(null);
export const chatSessionsAtom = atomWithStorage<ChatSession[]>("chatSessions", chatSessionListDefault);
export const chatFavoritesAtom = atomWithStorage<{ chatSessionId: string, created: Date, updated: Date, isSync?: boolean }[]>("chatFavorites", [{ chatSessionId: chatSessionListDefault?.[0]?.id || '', created: new Date(), updated: new Date() }]);
export const chatConversationTriggerAtom = atom<{ scrollToBottom?: boolean, scrollToId?: string }>({ scrollToBottom: false });
export const chatSessionsSettingsAtom = atom(false)
export const chatUpdateTriggerAtom = atom<{
  chatSessionId: string, loading?: boolean,
  update?: RouterInputs['chat']['updateChatSession']
  delete?: RouterInputs['chat']['deleteChatSession'],
  addMessage?: RouterInputs['chat']['addMessage'],
  deleteMessage?: RouterInputs['chat']['deleteMessage'],
  updateMessage?: RouterInputs['chat']['updateMessage']
}[]>([])


//~ SELECTOR
export const chatSessionActiveInfoAtom = atom(
  (get): ChatSession | null => {
    const id = get(chatSessionActiveIdAtom);
    const sessions = get(chatSessionsAtom);
    const session = sessions.find(s => s.id === id) || null;
    if (!session) return null;
    const { messages: _messages, ...allSession } = session;
    return { ...allSession, messages: [] } satisfies ChatSession
  })
export const chatSessionActiveAtom = atom(
  (get) => {
    const id = get(chatSessionActiveIdAtom);
    const sessions = get(chatSessionsAtom);
    return sessions.find(s => s.id === id) || null;
  },
  (get, set, session: Partial<ChatSession>) => {
    if (session) {
      const sessions = get(chatSessionsAtom);
      const sessionActiveIdAtom = get(chatSessionActiveIdAtom);
      const sessionIdUpdate = session.id || sessionActiveIdAtom;
      const newSessions = produce(sessions, draft => {
        const find = draft.find(s => s.id === sessionIdUpdate);
        if (find) {
          if (session?.id) find.id = session.id;
          if (session.messages)
            find.messages = [
              ...session.messages || []
            ]
          if (session.name) find.name = session.name;
          if (session.created) find.created = session.created;
          if (session.updated) find.updated = session.updated;
          if (session.defaultModel) find.defaultModel = session.defaultModel;
          if (session.lastSync) find.lastSync = session.lastSync;
          if (session.order) find.order = session.order;
          if (session.orderUpdated) find.orderUpdated = session.orderUpdated;
          if (session.defaultInstruction) find.defaultInstruction = session.defaultInstruction;
          if (session.isStreaming || session.isStreaming === false) find.isStreaming = session.isStreaming;
          if (session.isSync || session.isSync === false) find.isSync = session.isSync;
          if (session.isInit || session.isInit === false) find.isInit = session.isInit;
          if (session.loading || session.loading === false) find.loading = session.loading;
        }
        /*  else {
            draft.push(session);
        } */
      })
      set(chatSessionsAtom, newSessions);
    }
  }
)


//~ MUTATION
export const chatSessionsAddNewAtom = atom(
  null,
  (get, set, params?: {
    isAllowAddChatSession: {
      allow: boolean;
      allowSync: boolean;
      message: string;
    },
    defaultModel?: ChatModel
  }) => {
    const newId = generateNanoId(14)
    const { isAllowAddChatSession, defaultModel } = params || {}

    // If user still allow to add new chat session
    if (!isAllowAddChatSession?.allow) {
      toast.error(isAllowAddChatSession?.message || 'You have reached the limit of chats.')
      return
    }
    const teamActive = get(teamActiveAtom)
    const user = get(userAtom)
    set(chatSessionsAtom, prev => {
      const prevFixed = prev || []
      const firstSession = prevFixed.length > 0 ? prevFixed[0] : null
      return [
        {
          id: newId,
          localId: newId,
          name: 'New Chat',
          defaultModel: defaultModel || openAiChatModels[0]?.value,
          messages: [],
          created: new Date(),
          updated: new Date(),
          ...teamActive && { teamId: teamActive.id },
          ...user && { userId: user.id },
          isStreaming: true,


          isSync: false,
          isInit: true,
          //order: prevSession ? (prevSession?.order || 0) + 100 : 100,
          order: firstSession?.order ? firstSession.order - 1 : 100,
          orderUpdated: new Date(),
        },
        ...prevFixed,
      ]
    })
    return newId
  }
)
export const chatSessionUpdateAtom = atom(
  null,
  (get, set, session: Partial<ChatSession>) => {
    if (session?.id) {
      const sessions = get(chatSessionsAtom);
      const newSessions = produce(sessions, draft => {
        const find = draft.find(s => s.id === session.id);
        if (find) {
          if (session.messages) find.messages = session.messages;
          if (session.name) find.name = session.name;
          if (session.created) find.created = session.created;
          if (session.defaultModel) find.defaultModel = session.defaultModel;
        }
      })
      set(chatSessionsAtom, newSessions);
    }
  }
)

export const chatSendMessageAtom = atom(
  null,
  async (get, set, message: ChatMessage, _apiKey?: string) => {
    const session = get(chatSessionActiveAtom);
    if (!session) return null;

    // Get the model and provider
    const modelFixed = message?.model || session?.defaultModel || DEFAULT_MODEL;
    const provider = getModelProvider(modelFixed);

    // Get all API keys and check if the required one exists
    const allKeys = getAllApiKeys(get);
    // Look the key up by provider rather than chaining ternaries — the chain
    // silently fell through to the OpenAI key for any provider added later.
    const requiredKey = (provider && allKeys[provider]) || allKeys.openai;

    if (!requiredKey) {
      const providerNames: Record<string, string> = {
        openai: 'OpenAI',
        anthropic: 'Anthropic',
        google: 'Google AI',
        xai: 'xAI (Grok)',
        deepseek: 'DeepSeek'
      };
      const providerName = providerNames[provider || 'openai'] || 'Provider';
      toast.error(`Please add your ${providerName} API key`);
      set(apiKeyModalAtom, provider || 'openai');
      return null;
    }

    const isStreaming = !!session?.isStreaming;
    await handleSendMessageInit(
      get,
      set,
      session,
      messageToMessageToken(message),
      'client',
      isStreaming
    );
  }
)

export const chatSendMessageCloudAtom = atom(
  null,
  async (get, set, message: ChatMessage, _apiKey?: string) => {
    const session = get(chatSessionActiveAtom);
    if (!session) return null;

    // For cloud mode, we still check if API key exists (cloud will use team credential)
    const modelFixed = message?.model || session?.defaultModel || DEFAULT_MODEL;
    const provider = getModelProvider(modelFixed);

    // Get all API keys - for cloud, the backend will use team credentials if local key is missing
    const allKeys = getAllApiKeys(get);
    const hasLocalKey = (provider && allKeys[provider]) || allKeys.openai;

    // For cloud mode, we don't strictly require local key as backend uses team credentials
    // But we still want to warn if streaming with no local key
    const isStreaming = !!session?.isStreaming;

    if (isStreaming && !hasLocalKey) {
      const providerNames: Record<string, string> = {
        openai: 'OpenAI',
        anthropic: 'Anthropic',
        google: 'Google AI',
        xai: 'xAI (Grok)',
        deepseek: 'DeepSeek'
      };
      const providerName = providerNames[provider || 'openai'] || 'Provider';
      toast.error(`Please add your ${providerName} API key for streaming`);
      set(apiKeyModalAtom, provider || 'openai');
      return null;
    }

    await handleSendMessageInit(
      get,
      set,
      session,
      messageToMessageToken(message),
      'cloud',
      isStreaming
    );
  }
)

export const chatResolvePendingToolAtom = atom(
  null,
  async (get, set, input: { sessionId: string; toolCall: ToolCall; decision: 'approve' | 'reject' }) => {
    const sessions = get(chatSessionsAtom)
    const session = sessions.find(s => s.id === input.sessionId)
    const user = get(userAtom)
    if (!session) return null

    const suggestedArgs =
      input.toolCall.result
        && typeof input.toolCall.result === 'object'
        && (input.toolCall.result as { suggestedArgs?: unknown }).suggestedArgs
        && typeof (input.toolCall.result as { suggestedArgs?: unknown }).suggestedArgs === 'object'
        ? (input.toolCall.result as { suggestedArgs: Record<string, unknown> }).suggestedArgs
        : input.toolCall.args

    const approvalMessage = input.decision === 'approve'
      ? [
        'HUMAN_CONFIRMATION: APPROVED',
        `Pending tool: ${input.toolCall.name}`,
        `Tool call id: ${input.toolCall.id}`,
        'Instruction: Continue your previous plan and execute the pending tool now using the same arguments and confirm=true.',
        `Arguments: ${JSON.stringify({ ...suggestedArgs, confirm: true })}`,
      ].join('\n')
      : [
        'HUMAN_CONFIRMATION: REJECTED',
        `Pending tool: ${input.toolCall.name}`,
        `Tool call id: ${input.toolCall.id}`,
        'Instruction: Do not execute this tool. Continue safely without this action and offer an alternative.',
      ].join('\n')

    const message: ChatMessage = {
      id: generateLocalIdByRole('user'),
      chatSessionId: session.id,
      content: {
        contentType: 'text',
        content: approvalMessage,
      },
      userId: user?.id || '',
      created: new Date(),
      senderRole: 'user',
      model: session.defaultModel,
    }

    if (session?.isSync && session?.teamId) {
      await set(chatSendMessageCloudAtom, message)
    } else {
      await set(chatSendMessageAtom, message)
    }

    toast.success(input.decision === 'approve' ? 'Approved and resumed agent flow' : 'Rejected and asked for safe alternative')
    return null
  }
)

export const chatGenerateTitleAtom = atom(
  null,
  async (get, set, sessionId: string, message?: Partial<ChatMessage>) => {
    const sessions = get(chatSessionsAtom);
    const session = sessions.find(s => s.id === sessionId);

    if (session) {
      const modelFixed = message?.model
        || session.defaultModel
        || DEFAULT_MODEL;
      const { gptMessages } = compileGptMessages(session, undefined, defaultModelGptLimit(modelFixed).maxWords)
      if (!session.isSync) {
        const allKeys = getAllApiKeys(get);
        const provider = getModelProvider(modelFixed);

        // Check if the required API key is set
        const requiredKey = (provider && allKeys[provider]) || allKeys.openai;

        if (!requiredKey) {
          const providerName = provider?.toUpperCase() || 'Provider';
          toast.error(`Please set ${providerName} API key first`);
          // Open the appropriate modal
          set(apiKeyModalAtom, provider || 'openai');
          return;
        }

        const gpt = await aiChatRequestHandler(
          allKeys,
          modelFixed,
          [
            ...gptMessages,
            {
              content: 'Generate a very short chat title for this conversation as character or personality less than 25 characters (prevent adding word Chat at the end and wrap with quote)): ',
              role: 'user',
            }
          ]
        );
        const gptContentString = getGPTContentToTextString(gpt.choices[0]?.message?.content || '')
        const newTitle = gptContentString?.replace('Chat', '')?.trim()?.replace(/^"|"$/g, '');
        set(chatSessionUpdateAtom, { name: newTitle, id: session.id })
      } else {
        const apiKey = get(apiKeyAtom)?.key;

        const messagePromptTitle: GPTChatMessage = {
          content: 'Generate a very short chat title for this conversation as character or personality less than 25 characters (prevent adding word Chat at the end and wrap with quote)): ',
          role: 'user',
        }

        const { newTitle } = await nextApiChatTitleGenerate({
          prevMessages: [
            ...gptMessages,
            messagePromptTitle,
          ],
          chatSessionId: session.id,
          defaultModel: modelFixed,
          apiKey,
          isUpdateTitle: true,
        })
        // set(chatUpdateTriggerAtom, prev => ([...prev, { chatSessionId: session.id, update: { id: session.id, name: newTitle || '' } }]))
        set(chatSessionUpdateAtom, { name: newTitle || '', id: session.id })
      }
    }
  }
)

export const chatAddMessageAtom = atom(
  null,
  (get, set, message: ChatMessage | ChatMessage[], sessionId?: string) => {
    let session: ChatSession | null | undefined;
    if (sessionId) {
      const sessions = get(chatSessionsAtom);
      session = sessions.find(s => s.id === sessionId);
    } else {
      session = get(chatSessionActiveAtom);
    }

    if (session) {
      const newSession = produce(session, draft => {
        if (Array.isArray(message)) {
          draft.messages.push(...message);
        } else {
          draft.messages.push(message);
        }
      })
      set(chatSessionActiveAtom, newSession)
    }
  }
)

export const chatRemoveMessageAtom = atom(
  null,
  // isSkip is for synced session to force remove message
  (get, set, id: string, sessionId?: string, isSkip?: boolean) => {
    let session: ChatSession | null | undefined;
    if (sessionId) {
      const sessions = get(chatSessionsAtom);
      session = sessions.find(s => s.id === sessionId);
    } else {
      session = get(chatSessionActiveAtom);
    }

    if (!session) {
      console.error('remove', { id, session })
      toast.error("Session not found");
      return null
    }

    if (session) {
      const chatSessionId = session.id;
      if (session.isSync && !session?.isInit && !isSkip) {
        set(chatUpdateTriggerAtom, prev => ([...prev, { chatSessionId, deleteMessage: { id, chatSessionId } }]))
      } else {
        const newSession = produce(session, draft => {
          draft.messages = draft.messages.filter(m => m.id !== id);
        })
        set(chatSessionActiveAtom, newSession)
      }
    }
  }
)

export const chatUpdateMessageAtom = atom(
  null,
  (get, set, message: Partial<ChatMessage>, param?: { chatSessionId?: string, isSkip?: boolean }) => {
    const { chatSessionId, isSkip } = param || {}
    let session: ChatSession | null | undefined;

    //~ VALIDATION
    if (chatSessionId) {
      const sessions = get(chatSessionsAtom);
      session = sessions.find(s => s.id === chatSessionId);
    } else {
      session = get(chatSessionActiveAtom);
    }

    if (!session) {
      return null
    }

    //~ IF SYNC
    if (session?.isSync && !session?.isInit && !isSkip) {
      const chatSessionId = session.id;
      if (chatSessionId && message?.id) {
        set(chatUpdateTriggerAtom, prev => ([
          ...prev,
          {
            chatSessionId,
            updateMessage: {
              ...message,
              chatSessionId,
              id: message.id || '',
              ...message.content?.content
                ? {
                  content: {
                    content: message.content?.content || '',
                    contentType: 'text'
                  },
                  totalTokens: messageContentTokens(message.content),
                }
                : {
                  content: undefined
                }
            }
          }
        ]))
      }
    } else {
      //~ IF NOT SYNC
      set(chatSessionActiveAtom, produce(session, draft => {
        const find = draft.messages.find(m => m.id === message.id);
        if (find && message) {
          Object.assign(find, {
            ...message,
            ...message.content ? {
              totalTokens: messageContentTokens(message.content)
            } : {},
            updated: new Date()
          })
        }
      }))
    }
  }
)

export const chatRegenerateMessageAtom = atom(
  null,
  async (get, set, update: { sessionId?: string, messageId?: string, messageContent?: ChatMessage['content'] }, apiKeyPassed?: string) => {
    const { sessionId, messageId } = update
    let session: ChatSession | null | undefined;
    if (sessionId) {
      const sessions = get(chatSessionsAtom);
      session = sessions.find(s => s.id === sessionId);
    } else {
      session = get(chatSessionActiveAtom);
    }

    // Get all API keys
    const allKeys = getAllApiKeys(get);
    const modelFixed = session?.defaultModel || DEFAULT_MODEL;
    const provider = getModelProvider(modelFixed);

    // Build apiKey object with provider priority
    const apiKey = {
      ...allKeys,
      // If a specific key was passed, use it for the appropriate provider
      ...(apiKeyPassed && provider === 'openai' ? { openai: apiKeyPassed } : {}),
    };

    if (session && session.messages.length > 0) {
      const sessionMessages = [...session.messages]
      const messageIdx = messageId
        ? sessionMessages.findIndex(m => m.id === messageId)
        : sessionMessages.length - 1

      if (messageIdx === -1) {
        toast.error('Message not found')
        return;
      }
      const isStreaming = session?.isStreaming

      const message = { ...sessionMessages[messageIdx], ...update?.messageContent ? { content: update?.messageContent } : {} } as ChatMessage

      // get all messages before the message + the message
      const messagesBefore = [...sessionMessages.slice(0, messageIdx), message]

      // Current message or last message
      const currentMessage = messageId
        ? sessionMessages.find(m => m.id === messageId) as ChatMessage
        : sessionMessages[sessionMessages.length - 1] as ChatMessage

      const chatDefaultSettings = get(chatDefaultSettingsAtom)
      const defaultMaxTokensBySetting = (typeof chatDefaultSettings?.defaultMaxContextTokens === 'number' && chatDefaultSettings?.defaultMaxContextTokens)
        ? chatDefaultSettings?.defaultMaxContextTokens
        : defaultModelGptLimit(modelFixed).maxTokens

      const { gptMessages } = compileGptMessages({ ...session, messages: messagesBefore }, undefined, defaultMaxTokensBySetting)

      const chatSessionId = session.id

      let newAIMessage: ChatMessage | undefined;

      if (!currentMessage) return;

      // Remove the old AI response being regenerated (replace behavior like ChatGPT)
      // If the target message is an assistant message, remove it
      // If it's a user message, find and remove the next assistant message
      const targetMessage = sessionMessages[messageIdx];
      if (targetMessage?.senderRole === 'assistant') {
        set(chatRemoveMessageAtom, targetMessage.id, chatSessionId, true);
      } else if (targetMessage?.senderRole === 'user') {
        // Find the next assistant message after this user message
        const nextAssistantIdx = sessionMessages.findIndex(
          (m, idx) => idx > messageIdx && m.senderRole === 'assistant'
        );
        if (nextAssistantIdx !== -1) {
          const nextAssistant = sessionMessages[nextAssistantIdx];
          if (nextAssistant) {
            set(chatRemoveMessageAtom, nextAssistant.id, chatSessionId, true);
          }
        }
      }

      try {
        const regenerateAiMessageId = generateLocalIdByRole('ai');

        // Rebuild tools config for regenerate path so agent/tool behavior stays consistent
        const toolsConfig = get(globalToolsConfigAtom);
        const agentConfig = get(globalAgentConfigAtom);
        const agentMode = get(activeAgentModeAtom);
        const mcpServersUI = get(mcpServersConfigAtom);

        let toolsForMessage: ToolsForMessage | undefined;

        const hasBuiltinTools = toolsConfig.enabledTools.length > 0;
        const hasEnabledMCPServers = mcpServersUI.some(s => s.enabled);
        const toolCallsCollected: {
          id: string;
          name: string;
          args: Record<string, unknown>;
          result?: unknown;
          error?: string;
          status: 'pending' | 'running' | 'completed' | 'error';
          startedAt: Date;
          completedAt?: Date;
          duration?: number;
        }[] = [];

        if (supportsToolCalling(modelFixed) && (hasBuiltinTools || hasEnabledMCPServers)) {
          const tools: NonNullable<ToolsForMessage['tools']> = hasBuiltinTools ? (buildToolSet(toolsConfig) ?? {}) : {};

          if (hasEnabledMCPServers) {
            try {
              const mcpManager = getMCPManager();
              const enabledServers = mcpServersUI.filter(s => s.enabled);
              for (const server of enabledServers) {
                if (!mcpManager.isConnected(server.id)) {
                  await mcpManager.connect({
                    id: server.id,
                    name: server.name,
                    transport: { type: server.type, url: server.url },
                  });
                }
              }
              Object.assign(tools, mcpManager.getAllTools());
            } catch (mcpErr) {
              console.warn('⚠️ Failed to connect MCP servers (regenerate):', mcpErr);
            }
          }

          if (Object.keys(tools).length > 0) {
            toolsForMessage = {
              tools,
              toolChoice: 'auto',
              maxSteps: agentMode === 'agent' ? (agentConfig.maxSteps || 10) : 5,
              system: buildAgentSystemPrompt(agentMode),
              onToolCall: (toolName, args) => {
                const toolCallInfo = {
                  id: generateNanoId(8),
                  name: toolName,
                  args: args as Record<string, unknown>,
                  status: 'running' as const,
                  startedAt: new Date(),
                };
                toolCallsCollected.push(toolCallInfo);

                set(chatUpdateMessageAtom, {
                  id: regenerateAiMessageId,
                  loadingText: `Using ${toolName}...`,
                  toolCalls: toolCallsCollected.map(tc => ({
                    id: tc.id,
                    name: tc.name,
                    args: tc.args,
                    status: tc.status,
                    result: tc.result,
                    error: tc.error,
                    duration: tc.duration,
                    startedAt: tc.startedAt,
                    completedAt: tc.completedAt,
                  })),
                }, { chatSessionId: session?.id, isSkip: true });
              },
              onToolResult: (toolName, result) => {
                const existingCall = toolCallsCollected.find(tc => tc.name === toolName && tc.status === 'running');
                if (existingCall) {
                  existingCall.result = result;
                  if (isConfirmationRequiredResult(result)) {
                    existingCall.status = 'pending';
                    existingCall.error = result.message || 'Awaiting your approval to continue.';
                  } else {
                    existingCall.status = 'completed';
                    existingCall.error = undefined;
                  }
                  existingCall.completedAt = new Date();
                  existingCall.duration = existingCall.completedAt.getTime() - existingCall.startedAt.getTime();
                }

                set(chatUpdateMessageAtom, {
                  id: regenerateAiMessageId,
                  ...(isConfirmationRequiredResult(result) ? { loadingText: 'Pending your approval...' } : {}),
                  toolCalls: toolCallsCollected.map(tc => ({
                    id: tc.id,
                    name: tc.name,
                    args: tc.args,
                    status: tc.status,
                    result: tc.result,
                    error: tc.error,
                    duration: tc.duration,
                    startedAt: tc.startedAt,
                    completedAt: tc.completedAt,
                  })),
                }, { chatSessionId: session?.id, isSkip: true });
              },
            };
          }
        }

        //~ SET AI RESPONSE LOADING
        const abortController = new AbortController();
        const newAIMessage: ChatMessage = {
          id: regenerateAiMessageId,
          chatSessionId: chatSessionId,
          content: {
            contentType: 'text',
            content: '',
          },
          userId: '',
          senderRole: 'assistant',
          created: new Date(),
          loading: true,
          abortSignal: abortController,
        }

        //~ FETCHING
        if (session?.isSync && !session?.isInit) {
          const onMessage = handleSendMessageInitCloud(get, set, session, currentMessage, newAIMessage)
          const currentMsgContentString = Array.isArray(currentMessage?.content?.content)
            ? currentMessage?.content?.content.join(' ')
            : currentMessage?.content?.content
          await handleSendMessageFetchCloud(
            get,
            set,
            session,
            {
              ...currentMessage,
              totalTokens: countTokens(currentMsgContentString || ''),
            },
            newAIMessage,
            apiKey,
            gptMessages,
            modelFixed,
            abortController,
            onMessage,
            isStreaming,
            { messageAction: 'update' },
            { type: 'regenerate' },
            toolsForMessage
          )
        } else {
          console.log('STORE')
          set(chatAddMessageAtom, newAIMessage, chatSessionId)
          sessionMessages.push(newAIMessage)
          const modelSourceType = provider === 'anthropic' ? 'anthropic' : 'openai'
          const onMessage = handleSendMessageOnMessage(get, set, session, newAIMessage || currentMessage, undefined, modelSourceType)
          await handleSendMessageFetchClient(
            get,
            set,
            session,
            currentMessage,
            newAIMessage,
            apiKey,
            gptMessages,
            modelFixed,
            abortController,
            onMessage,
            isStreaming,
            toolsForMessage
          )
        }

        handleSendMessagePostFetch(get, set, chatSessionId)

        //  set(chatSessionActiveAtom, newSessionReply);
      } catch (error: unknown) {
        if (error instanceof Error && error.message === 'canceled') {
          return;
        }
        void handleSendMessageError(get, set, session, newAIMessage?.id || '', error)
      }

    }
  }
)

